import { AsYouType, getCountryCallingCode, type CountryCode } from 'libphonenumber-js/max';

export interface PhoneInputDerivedState {
  country: CountryCode;
  nationalDisplayText: string;
  emittedValue: string;
}

function keepDigitsAndLeadingPlus(rawText: string): string {
  const hasLeadingPlus = rawText.trim().startsWith('+');
  const digitsOnly = rawText.replace(/\D/g, '');
  return hasLeadingPlus ? '+' + digitsOnly : digitsOnly;
}

function deriveNationalSignificantDigits(formatter: AsYouType, strippedText: string): string {
  const parsedNumber = formatter.getNumber();
  if (parsedNumber !== undefined) {
    return String(parsedNumber.nationalNumber);
  }
  if (strippedText.startsWith('+') === false) {
    return strippedText;
  }
  const detectedCallingCode = formatter.getCallingCode();
  const digitsOnly = strippedText.replace(/\D/g, '');
  if (detectedCallingCode === undefined) {
    return digitsOnly;
  }
  return digitsOnly.startsWith(detectedCallingCode)
    ? digitsOnly.slice(detectedCallingCode.length)
    : digitsOnly;
}

function deriveNationalDisplayText(
  formatter: AsYouType,
  strippedText: string,
  resolvedCountry: CountryCode,
  nationalSignificantDigits: string
): string {
  const parsedNumber = formatter.getNumber();
  if (
    parsedNumber !== undefined &&
    parsedNumber.country === resolvedCountry &&
    parsedNumber.isValid()
  ) {
    return parsedNumber.formatNational();
  }
  const isTypedNationally =
    strippedText.startsWith('+') === false && strippedText.startsWith('00') === false;
  const nationalFormatter = new AsYouType(resolvedCountry);
  return nationalFormatter.input(isTypedNationally ? strippedText : nationalSignificantDigits);
}

function buildTypedInternationalString(country: CountryCode, nationalDigits: string): string {
  if (nationalDigits.length === 0) {
    return '';
  }
  const callingCode = getCountryCallingCode(country);
  const internationalFormatter = new AsYouType();
  return internationalFormatter.input('+' + callingCode + nationalDigits);
}

export function derivePhoneInputState(
  rawText: string,
  currentCountry: CountryCode
): PhoneInputDerivedState {
  const strippedText = keepDigitsAndLeadingPlus(rawText);
  if (strippedText.length === 0) {
    return { country: currentCountry, nationalDisplayText: '', emittedValue: '' };
  }

  const formatter = new AsYouType(currentCountry);
  formatter.input(strippedText);
  const detectedCountry = formatter.getCountry();
  const resolvedCountry = detectedCountry === undefined ? currentCountry : detectedCountry;

  const nationalSignificantDigits = deriveNationalSignificantDigits(formatter, strippedText);
  const nationalDisplayText = deriveNationalDisplayText(
    formatter,
    strippedText,
    resolvedCountry,
    nationalSignificantDigits
  );

  const numberValue = formatter.getNumberValue();
  const emittedValue =
    numberValue === undefined
      ? buildTypedInternationalString(resolvedCountry, nationalSignificantDigits)
      : numberValue;

  return { country: resolvedCountry, nationalDisplayText, emittedValue };
}
